import { MsalProvider } from "@azure/msal-react";
import { ReactElement, useMemo } from "react";
import { PublicClientApplication, LogLevel } from "@azure/msal-browser";
import { useNavigate } from "react-router-dom";
import { CustomNavigationClient } from "../utils/CustomNavigationClient";
import { config } from "@shared/envVariables";
import { useQuery } from "@tanstack/react-query";
import { LoadingState } from "@shared/common/components/State/LoadingState";
import { ErrorState } from "@shared/common/components/State/ErrorState";

interface IAuthenticationProviderProps {
  readonly children: ReactElement;
}

export function AuthenticationProvider({
  children,
}: IAuthenticationProviderProps): React.JSX.Element {
  const navigate = useNavigate();

  const msalInstance = useMemo(
    () =>
      new PublicClientApplication({
        cache: {
          cacheLocation: "sessionStorage",
        },
        auth: {
          clientId: config.azureAd.clientId,
          authority: `https://${config.azureAd.instance}/${config.azureAd.domain}/`,
          // NOTE: Must point to the dedicated MSAL redirect bridge page (authentication/login-callback.html),
          // not an SPA route. This is required by MSAL Browser v5 for silent/popup token acquisition to
          // work correctly in a hidden iframe. See docker/nginx.conf for the matching server config, and
          // https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/redirect-bridge.md
          redirectUri: "/authentication/login-callback.html",
          postLogoutRedirectUri: "/",
        },
        system: {
          loggerOptions: {
            logLevel: LogLevel.Warning,
            loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
              if (containsPii) {
                return;
              }
              switch (level) {
                case LogLevel.Error:
                  console.error(message);
                  return;
                case LogLevel.Info:
                  console.info(message);
                  return;
                case LogLevel.Verbose:
                  console.debug(message);
                  return;
                case LogLevel.Warning:
                  console.warn(message);
                  return;
              }
            },
          },
        },
      }),
    [],
  );

  const navigationClient = new CustomNavigationClient(navigate);
  msalInstance.setNavigationClient(navigationClient);

  // NOTE: Use react query to ensure msal client is intitialized before other components try to
  // acquire tokens. Show loading state in the meanwhile.
  const { isLoading, isError, error } = useQuery({
    queryKey: ["msal-client"],
    queryFn: () => msalInstance.initialize().then(() => "OK"), // Provide a value so react query doesn't fail on undefined data
    staleTime: Infinity, // Don't refetch the "initialize"
  });
  if (isLoading) return <LoadingState fullPage />;
  if (isError) {
    console.error("Error initializing MSAL instance: ", error);
    return <ErrorState />;
  }

  return <MsalProvider instance={msalInstance}>{children}</MsalProvider>;
}
